import { useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';
import type { QrCodeType } from '@change-corgi/middle-end-gql-schema';

import { usePetitionQRCode } from '#src/app/shared/hooks/qrcode';

import { type FlyerPreviewImage, useFlyerPreviewImage } from './useFlyerPreviewImage';
import { usePetitionTitleImage } from './usePetitionTitleImage';

const QUERY_KEY = 'shared-flyer-a-pdf';

const PNG_DATA_URL_PREFIX = 'data:image/png;base64,';

export type { FlyerPreviewImage };

type FlyerAStatus = 'preparing' | 'ready' | 'failed';

type FlyerATrackingData = Readonly<Record<string, boolean | string | number | undefined>>;

type UseFlyerAArgs = {
	petitionId: string;
	petitionTitle: string;
	/**
	 * The QR code type printed on the flyer; it sets the medium the scans are attributed to.
	 */
	qrCodeType: QrCodeType;
	/**
	 * The consumer's share tracking data (`context`, `view_context`, `petition_id`, ...), merged into
	 * the event fired by `trackShare`.
	 */
	getTrackingData: () => FlyerATrackingData;
	/** The Sentry `feature` tag of the hook's error reports. */
	feature: string;
};

type UseFlyerAReturn = ModelHookResult<
	{
		status: FlyerAStatus;
		/**
		 * The Flyer A PDF, set once `status` is `ready`. It is built ahead of any user action so it can
		 * be handed to `navigator.share` synchronously from a tap handler.
		 */
		file: File | undefined;
		previewImage: FlyerPreviewImage | undefined;
		/**
		 * The share id the flyer's QR code carries, once the QR code loaded. For a consumer that tracks
		 * the flyer share with its own event instead of `trackShare`.
		 */
		shareId: string | null | undefined;
	},
	{
		/**
		 * Fires `share_petition` for a flyer download/share, as on the starter dashboard's flyer download,
		 * with the QR code's generated share id.
		 */
		trackShare: () => void;
		handlePreviewImageError: () => void;
	}
>;

function getStatus({ ready, failed }: { ready: boolean; failed: boolean }): FlyerAStatus {
	if (ready) return 'ready';
	if (failed) return 'failed';
	return 'preparing';
}

/**
 * Builds the Flyer A poster PDF for a petition as soon as its QR code and title image are ready.
 * The QR code query and the title image start when the hook mounts, and the title image loads the
 * renderers chunk, PDF library included. The PDF build starts once both exist.
 *
 * All three stay cached for the app session (`'static'` with no garbage collection, on a query
 * client that lives as long as the app), so a remounted consumer gets the same QR code, share id,
 * and file without a new request or build, and a build that finishes after the consumer unmounts is
 * there for the next mount. A client-side navigation back to the same petition reuses them too.
 */
export function useFlyerA({
	petitionId,
	petitionTitle,
	qrCodeType,
	getTrackingData,
	feature,
}: UseFlyerAArgs): UseFlyerAReturn {
	const { translate } = useI18n();
	const { errorReporter } = useUtilityContext();
	const track = useTracking();

	// Each QR request mints a short link and share id. The app's client refetches on every mount and
	// drops unobserved data at once, so both options are needed to keep one per app session.
	const qrCodeState = usePetitionQRCode({
		slugOrId: petitionId,
		type: qrCodeType,
		staleTime: 'static',
		gcTime: Infinity,
	});
	const titleImageState = usePetitionTitleImage({ title: petitionTitle, petitionId, feature });
	const { previewImage, handlePreviewImageError } = useFlyerPreviewImage();

	const qrCodeDataUrl = qrCodeState.data?.imageBase64Uri;
	const shareId = qrCodeState.data?.shareId;
	const qrCodeRequestId = qrCodeState.data?.requestId;
	const petitionTitleDataUrl = titleImageState.data;

	// FIXME: key names still reference the starter dashboard; kept as-is so translated locales keep the strings
	const scanToSign = translate(
		'corgi.petition.starter_dashboard.grow_petition.share_petition_offline.flyer_qrcode_label',
	);
	const filename = translate('corgi.petition.starter_dashboard.shared.flyer.filename', { templateName: 'flyer-a' });

	// Keyed on the inputs the data URLs come from rather than the large data URLs themselves. A new QR
	// response (new `dataUpdatedAt`) rebuilds the PDF; the title image follows from `petitionTitle`.
	// eslint-disable-next-line @tanstack/query/exhaustive-deps -- the key covers the data URLs' inputs
	const pdfState = useQuery({
		queryKey: [
			QUERY_KEY,
			{
				petitionId,
				qrCodeType,
				shareId,
				qrCodeUpdatedAt: qrCodeState.dataUpdatedAt,
				petitionTitle,
				scanToSign,
				filename,
			},
		],
		queryFn: async () => {
			if (!qrCodeDataUrl || !petitionTitleDataUrl) throw new Error('Flyer A inputs are not ready');
			try {
				// The PDF library only warns about an image it cannot decode, and builds a poster with an empty QR box.
				if (!qrCodeDataUrl.startsWith(PNG_DATA_URL_PREFIX) || qrCodeDataUrl.length === PNG_DATA_URL_PREFIX.length) {
					throw new Error('The Flyer A QR code is not a PNG data URL');
				}
				const { renderFlyerAPdf } = await import(/* webpackChunkName: "cmp/flyer_a" */ './renderers');
				const blob = await renderFlyerAPdf({ petitionTitleDataUrl, qrCodeDataUrl, scanToSign });
				return new File([blob], `${filename}.pdf`, { type: 'application/pdf' });
			} catch (error) {
				void errorReporter.report({
					error: error instanceof Error ? error : new Error('Failed to renderFlyerAPdf', { cause: error }),
					params: { event: 'renderFlyerAPdf', petitionId },
					context: { feature, requestId: qrCodeRequestId },
				});
				throw error;
			}
		},
		enabled: !!qrCodeDataUrl && !!petitionTitleDataUrl,
		staleTime: 'static',
		gcTime: Infinity,
		retry: false,
	});

	const trackShare = useCallback(() => {
		void track('share_petition', {
			...getTrackingData(),
			location: 'qr_code_flyer_download',
			content: 'flyer-a',
			...(shareId != null ? { generated_share_id: shareId, generated_share_id_source: 'qr_code_server' as const } : {}),
		});
	}, [getTrackingData, shareId, track]);

	return {
		data: {
			status: getStatus({
				ready: !!pdfState.data,
				failed: qrCodeState.isError || titleImageState.isError || pdfState.isError,
			}),
			file: pdfState.data,
			previewImage,
			shareId,
		},
		actions: { trackShare, handlePreviewImageError },
	};
}
